// Elintra — Utils & Constants

export const cn = (...classes: Array<string | undefined | null | false>): string =>
  classes.filter(Boolean).join(' ');

export const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

// Decimal STRINGS (amount / algo_settled_usdt) are never parsed through a float.
// Sum them by integer-cents arithmetic on the string representation, then format.

const toCents = (decimal: string): number => {
  const negative = decimal.trim().startsWith('-');
  const cleaned  = decimal.trim().replace(/^[-+]/, '');
  const [whole = '0', frac = ''] = cleaned.split('.');
  const wholeDigits = whole.replace(/[^0-9]/g, '') || '0';
  const fracDigits  = `${frac}00`.slice(0, 2).replace(/[^0-9]/g, '').padEnd(2, '0');
  const cents = Number(wholeDigits) * 100 + Number(fracDigits);
  return Number.isFinite(cents) ? (negative ? -cents : cents) : 0;
};

const centsToDecimal = (cents: number): string => {
  const sign  = cents < 0 ? '-' : '';
  const abs   = Math.abs(cents);
  const whole = Math.trunc(abs / 100);
  const frac  = abs % 100;
  return `${sign}${whole}.${String(frac).padStart(2, '0')}`;
};

// Sum a list of decimal money STRINGS, returning a decimal STRING (no float math).
export const sumMoneyStrings = (values: Array<string | null | undefined>): string =>
  centsToDecimal(values.reduce<number>((acc, v) => acc + (v ? toCents(v) : 0), 0));

// Format a decimal money STRING as USD (en-US grouping) without float parsing.
export const formatMoneyString = (decimal: string): string => {
  const cents  = toCents(decimal);
  const sign   = cents < 0 ? '-' : '';
  const abs    = Math.abs(cents);
  const whole  = Math.trunc(abs / 100).toLocaleString('en-US');
  const frac   = String(abs % 100).padStart(2, '0');
  return `${sign}$${whole}.${frac}`;
};

export const formatTimestamp = (iso: string): string =>
  new Date(iso).toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3,
  });

export const formatDateLabel = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export const formatLastSync = (iso: string): string => {
  const diffMs  = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1)  return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24)  return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
};

export const COLORS = {
  primary:   '8B5CF6',
  success:   '10B981',
  warning:   'F59E0B',
  danger:    'EF4444',
  surface:   '18181B',
  border:    '27272A',
  text:      'FAFAFA',
  textMuted: 'A1A1AA',
  acp:       '3B82F6',
} as const;

export const PROTOCOL_COLORS: Record<string, string> = {
  A2A:  `#${COLORS.primary}`,
  ACP:  `#${COLORS.acp}`,
  UCP:  `#${COLORS.success}`,
  NLIP: `#${COLORS.warning}`,
};
